/**
 * CalendarPageMobileCommandBar Component
 * 
 * Purpose:
 * Mobile command bar component that provides sticky navigation controls below the global nav
 * on mobile devices. Includes month navigation (prev/next/today). Only renders on mobile viewport (isMobile === true).
 * 
 * High-level Workflow:
 * 1. Receives UI state and handlers as props.
 * 2. Conditionally renders only on mobile (isMobile === true).
 * 3. Displays month navigation cluster (prev/next/today buttons, month label).
 * 4. All buttons are disabled when no property is selected (hasProperty === false).
 * 
 * Assumptions & Invariants:
 * - Mobile-only: Component only renders when isMobile === true. Desktop view uses header card
 *   with month selector instead.
 * - Sticky positioning: Command bar is sticky below global nav, providing persistent navigation
 *   controls while scrolling calendar content.
 * - Month navigation: Prev/next buttons change calendar month. Today button navigates to current
 *   month and clears selected date. Month label displays current month/year.
 * - Button states: All navigation buttons are disabled when hasProperty === false to prevent
 *   navigation without a selected property.
 * 
 * Dependencies:
 * - Internal:
 *   - None (pure presentational component)
 * - External:
 *   - react: None (functional component, no hooks)
 * 
 * Version: 1.0.0
 * 
 * Change Log:
 * - 2026-01-26: Initial extraction from CalendarPage.jsx (Phase 5 refactoring)
 */

/**
 * Mobile command bar component
 * 
 * @param {boolean} isMobile - Whether mobile layout is active (component hidden if false)
 * @param {boolean} hasProperty - Whether a property is selected
 * @param {string} monthYearLabel - Formatted month/year label (e.g., "January 2026")
 * @param {Function} handlePrevMonth - Handler for previous month navigation
 * @param {Function} handleNextMonth - Handler for next month navigation
 * @param {Function} handleToday - Handler for today navigation
 * @param {Object} styles - Styles object from CalendarPage
 */
export default function CalendarPageMobileCommandBar({
  isMobile,
  hasProperty,
  monthYearLabel,
  handlePrevMonth,
  handleNextMonth,
  handleToday,
  styles,
}) {
  // Only render on mobile
  if (!isMobile) {
    return null;
  }

  return (
    <div style={styles.getMobileCommandBarStyle()}>
      <div style={styles.mobileCommandBarContent}>
        {/* PHASE 7A: Reordered - Month navigation primary, Property secondary */}
        {/* PHASE 11: Enhanced navigation with Today button */}
        {/* B2: One-thumb navigation cluster - grouped for thumb reach */}
        <div style={styles.mobileCommandBarLeft}>
          {/* B2: Navigation cluster container */}
          <div style={styles.mobileNavCluster}>
            <button
              onClick={handlePrevMonth}
              style={styles.mobileNavButton}
              aria-label="Previous month"
              disabled={!hasProperty}
            >
              ‹
            </button>
            <span style={styles.mobileMonthLabel} title={monthYearLabel}>{monthYearLabel}</span>
            <button
              onClick={handleNextMonth}
              style={styles.mobileNavButton}
              aria-label="Next month"
              disabled={!hasProperty}
            >
              ›
            </button>
            {/* PHASE 11: Today button - discoverable and mobile-appropriate */}
            <button
              onClick={handleToday}
              style={styles.mobileTodayButton}
              aria-label="Go to today"
              title="Go to today"
              disabled={!hasProperty}
            >
              Today
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
